import { Stack, Table, Text } from '@mantine/core';
import { Loader } from 'lucide-react';
import type { CostPreviewResponse } from '../../hooks/useBuilder';
import { colors } from '../../theme/tokens';

/**
 * CostPane — renders the result of `useBuilderCostPreview`. Pure presentation:
 * the builder owns the query and passes loading + cost in. Shows the headline
 * hourly rate, an 8-hour session estimate, and a per-VM breakdown table.
 */
export interface CostPaneProps {
  isLoading: boolean;
  cost: CostPreviewResponse | undefined;
}

export function CostPane({ isLoading, cost }: CostPaneProps) {
  if (isLoading) {
    return (
      <Stack gap="sm" align="center" py="lg">
        <Loader size={20} color={colors.hudCyan} />
        <Text size="sm" c="dimmed">
          Computing cost...
        </Text>
      </Stack>
    );
  }
  if (!cost) return null;

  const { hourly_usd, eight_hour_usd, breakdown, estimated_for } = cost;
  return (
    <Stack gap="sm">
      <Stack gap={4}>
        <Text fw={600} size="sm" c={colors.neon}>
          ${hourly_usd.toFixed(4)}/hour
        </Text>
        <Text size="xs" c="dimmed">
          ≈ ${eight_hour_usd.toFixed(2)} for 8-hour session
        </Text>
      </Stack>
      {breakdown.length > 0 && (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>VM</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Count</Table.Th>
              <Table.Th>$/hr</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {breakdown.map((row) => (
              <Table.Tr key={row.name}>
                <Table.Td ff="JetBrains Mono">{row.name}</Table.Td>
                <Table.Td ff="JetBrains Mono">{row.instance_type}</Table.Td>
                <Table.Td>{row.count}</Table.Td>
                <Table.Td>${row.line_hourly_usd.toFixed(4)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {estimated_for.length > 0 && (
        <Text size="xs" c="yellow">
          *Estimated for {estimated_for.join(', ')} (rate not in pricing table)
        </Text>
      )}
    </Stack>
  );
}
